import type { Camp } from '@orbomb/content';
import type { SimConfig } from '../config';
import { CAMP_ORDER } from '../constants';
import type { EventSink } from '../events';
import { computeCross, inBand, inCross, nearestFreeCell } from '../geometry';
import { nextInt } from '../rng';
import type { ExplosionState, GoldenBallState, MatchState } from '../types';

export function contactArmingAllowed(state: MatchState): boolean {
  return state.clock.phase === 'REGULATION' || state.clock.phase === 'SUDDEN_DEATH';
}

function arm(
  state: MatchState,
  config: SimConfig,
  golden: GoldenBallState,
  camp: Camp | null,
  sink: EventSink,
) {
  const tick = sink.tick;
  golden.status = 'ARMED';
  golden.armedAtTick = tick;
  golden.detonationTick = tick + config.ticks.fuse;
  golden.firstToucher = camp;
  golden.lastToucher = camp;
  golden.armedBy = camp ? 'CONTACT' : 'AUTO';
  golden.chainedByExplosionId = null;
  state.stats.armings += 1;
  if (!camp) state.stats.autoArmings += 1;
  state.stats.firstArmTick ??= tick;
  sink.emit({
    type: 'ARMED',
    goldenId: golden.id,
    camp,
    auto: camp === null,
    detonationTick: golden.detonationTick,
  });
}

/**
 * Contact arming. Only a player able to arm (NORMAL or PROTECTED, not
 * frozen) touching a NEUTRAL ball arms it. Later contacts never reset the fuse; they only
 * update the last toucher. Players are processed ORANGE first, golden balls by index.
 */
export function resolveContactArming(
  state: MatchState,
  config: SimConfig,
  contacts: Uint8Array,
  frozen: readonly [boolean, boolean],
  sink: EventSink,
) {
  const allowed = contactArmingAllowed(state);
  const count = state.goldens.length;
  for (let p = 0; p < 2; p++) {
    const player = state.players[p];
    if (!player || frozen[p] || player.status === 'STUNNED') continue;
    for (let g = 0; g < count; g++) {
      if (contacts[p * count + g] !== 1) continue;
      const golden = state.goldens[g] as GoldenBallState;
      if (golden.status === 'NEUTRAL' && allowed) {
        arm(state, config, golden, player.camp, sink);
      } else if (golden.status === 'ARMED' && golden.lastToucher !== player.camp) {
        golden.lastToucher = player.camp;
        sink.emit({ type: 'TOUCHED', goldenId: golden.id, camp: player.camp });
      }
    }
  }
}

/** Sudden-death escalation: automatically arm one NEUTRAL ball every interval. */
export function resolveAutoArming(state: MatchState, config: SimConfig, sink: EventSink) {
  const clock = state.clock;
  if (clock.phase !== 'SUDDEN_DEATH' || clock.nextAutoArmTick === null) return;
  if (sink.tick < clock.nextAutoArmTick) return;
  const candidates = state.goldens.filter((g) => g.status === 'NEUTRAL');
  if (candidates.length === 0) {
    clock.nextAutoArmTick = sink.tick + 1;
    return;
  }
  const golden = candidates[nextInt(state, candidates.length)] as GoldenBallState;
  arm(state, config, golden, null, sink);
  clock.nextAutoArmTick = sink.tick + config.ticks.suddenDeathAutoArmInterval;
}

export function unlimitedRangeActive(state: MatchState, config: SimConfig, tick: number): boolean {
  const start = state.clock.suddenDeathStartTick;
  return (
    state.clock.phase === 'SUDDEN_DEATH' &&
    start !== null &&
    tick - start >= config.ticks.suddenDeathUnlimitedRangeAfter
  );
}

export function currentRange(state: MatchState, config: SimConfig, tick: number): number {
  return unlimitedRangeActive(state, config, tick)
    ? config.explosion.unlimitedRange
    : config.explosion.range;
}

/** Cross an armed ball would produce right now (used for previews and bots). */
export function previewCross(state: MatchState, config: SimConfig, golden: GoldenBallState) {
  const cell = nearestFreeCell(config, golden.x, golden.y);
  return computeCross(config, cell.col, cell.row, currentRange(state, config, state.tick));
}

/** Detonates every armed ball due on this tick, in index order. */
export function resolveDetonations(state: MatchState, config: SimConfig, sink: EventSink) {
  const tick = sink.tick;
  for (const golden of state.goldens) {
    if (golden.status !== 'ARMED' || golden.detonationTick === null) continue;
    if (golden.detonationTick > tick) continue;
    const cell = nearestFreeCell(config, golden.x, golden.y);
    const cross = computeCross(config, cell.col, cell.row, currentRange(state, config, tick));
    const explosion: ExplosionState = {
      id: state.nextExplosionId++,
      sourceGoldenId: golden.id,
      cross,
      startedAtTick: tick,
      lethalUntilTick: tick + config.ticks.lethal,
      visualUntilTick: tick + config.ticks.visual,
      chained: golden.chainedByExplosionId !== null,
      firstToucher: golden.firstToucher,
      lastToucher: golden.lastToucher,
      hitTargets: [],
    };
    state.explosions.push(explosion);
    state.stats.explosions += 1;
    if (explosion.chained) state.stats.chainDetonations += 1;
    sink.emit({
      type: 'DETONATED',
      goldenId: golden.id,
      explosionId: explosion.id,
      chained: explosion.chained,
    });
    sink.emit({ type: 'EXPLOSION_STARTED', explosionId: explosion.id, cross });

    golden.status = 'ABSENT';
    golden.vx = 0;
    golden.vy = 0;
    golden.armedAtTick = null;
    golden.detonationTick = null;
    golden.firstToucher = null;
    golden.lastToucher = null;
    golden.armedBy = null;
    golden.chainedByExplosionId = null;
    golden.respawnAtTick = tick + config.ticks.absent;
    golden.activeAtTick = null;
  }
}

export type PendingDamage =
  | { kind: 'TOTEM'; camp: Camp; explosion: ExplosionState }
  | { kind: 'PLAYER'; camp: Camp; explosion: ExplosionState };

function sign(value: number, fallback: number): number {
  if (value > 0) return 1;
  if (value < 0) return -1;
  return fallback >= 0 ? 1 : -1;
}

/**
 * Evaluates every lethal explosion against every target, in explosion id order.
 * Deduplicates on (explosionId, targetId). Chains and ejections are applied immediately;
 * damage to players and totems is returned for ordered application.
 */
export function resolveExplosionTargets(
  state: MatchState,
  config: SimConfig,
  sink: EventSink,
): PendingDamage[] {
  const tick = sink.tick;
  const pending: PendingDamage[] = [];
  for (const explosion of state.explosions) {
    if (tick >= explosion.lethalUntilTick) continue;
    const { cross, hitTargets } = explosion;

    if (explosion.startedAtTick === tick) {
      for (const camp of cross.basesHit) {
        const id = camp === 'ORANGE' ? 'totem-orange' : 'totem-turquoise';
        if (hitTargets.includes(id)) continue;
        hitTargets.push(id);
        pending.push({ kind: 'TOTEM', camp, explosion });
      }
    }

    for (const camp of CAMP_ORDER) {
      const player = state.players[camp === 'ORANGE' ? 0 : 1];
      if (player.status !== 'NORMAL' || hitTargets.includes(player.id)) continue;
      if (!inCross(cross, player.x, player.y)) continue;
      hitTargets.push(player.id);
      pending.push({ kind: 'PLAYER', camp, explosion });
    }

    for (const golden of state.goldens) {
      if (golden.status !== 'ARMED' && golden.status !== 'NEUTRAL') continue;
      if (hitTargets.includes(golden.id) || !inCross(cross, golden.x, golden.y)) continue;
      hitTargets.push(golden.id);
      if (golden.status === 'ARMED' && golden.detonationTick !== null) {
        // A chain may bring a detonation forward, never delay it.
        const target = Math.min(golden.detonationTick, tick + config.ticks.chainDelay);
        if (target < golden.detonationTick) {
          sink.emit({
            type: 'CHAIN_ACCELERATED',
            goldenId: golden.id,
            explosionId: explosion.id,
            fromTick: golden.detonationTick,
            toTick: target,
          });
          golden.detonationTick = target;
          golden.chainedByExplosionId = explosion.id;
        }
      } else {
        // Neutral ball: ejected along the beam, stays NEUTRAL.
        const inH = inBand(cross.horizontal, golden.x, golden.y);
        const inV = inBand(cross.vertical, golden.x, golden.y);
        const dx = golden.x - (cross.col + 0.5);
        const dy = golden.y - (cross.row + 0.5);
        const alongX = inH && (!inV || Math.abs(dx) >= Math.abs(dy));
        const speed = config.explosion.neutralEjectionSpeed;
        golden.vx = alongX ? sign(dx, golden.vx) * speed : 0;
        golden.vy = alongX ? 0 : sign(dy, golden.vy) * speed;
        sink.emit({
          type: 'GOLDEN_EJECTED',
          goldenId: golden.id,
          explosionId: explosion.id,
          vx: golden.vx,
          vy: golden.vy,
        });
      }
    }
  }
  return pending;
}

export function pruneExplosions(state: MatchState, nextTick: number) {
  if (state.explosions.some((e) => e.visualUntilTick <= nextTick)) {
    state.explosions = state.explosions.filter((e) => e.visualUntilTick > nextTick);
  }
}
